import React, { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function CareerProfileSection({ profile }) {
  const queryClient = useQueryClient();
  const existing = profile.career_profile || {};

  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    current_industry: existing.current_industry || "IT Services & Consulting",
    department: existing.department || "Other",
    role_category: existing.role_category || "Other",
    job_role: existing.job_role || "Other",
    desired_job_type: existing.desired_job_type || "",
    desired_employment_type: existing.desired_employment_type || "",
    preferred_shift: existing.preferred_shift || "",
    preferred_locations: existing.preferred_locations || ["Hyderabad/Secunderabad"],
    expected_salary: existing.expected_salary || "",
  });

  const updateMutation = useMutation({
    mutationFn: (data) => backendApi.entities.UserProfile.updateCareer(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
      setIsEditing(false);
    },
  });

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  const data = profile.career_profile || formData;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Career profile</h3>
        <button
          onClick={() => setIsEditing(true)}
          className="text-blue-600 hover:underline text-sm font-medium"
        >
          {profile.career_profile ? "Edit" : "Add"}
        </button>
      </div>

      {/* Display Mode */}
      <div className="grid grid-cols-2 gap-y-6 gap-x-12">
        <div>
          <p className="text-sm text-gray-500">Current industry</p>
          <p className="text-gray-800">{data.current_industry}</p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Department</p>
          <p className="text-gray-800">{data.department}</p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Role category</p>
          <p className="text-gray-800">{data.role_category}</p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Job role</p>
          <p className="text-gray-800">{data.job_role}</p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Desired job type</p>
          <p className={data.desired_job_type ? "text-gray-800" : "text-blue-600"}>
            {data.desired_job_type || "Add desired job type"}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Desired employment type</p>
          <p
            className={
              data.desired_employment_type ? "text-gray-800" : "text-blue-600"
            }
          >
            {data.desired_employment_type || "Add desired employment type"}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Preferred shift</p>
          <p
            className={
              data.preferred_shift ? "text-gray-800" : "text-blue-600"
            }
          >
            {data.preferred_shift || "Add preferred shift"}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Preferred work location</p>
          <p className="text-gray-800">
            {data.preferred_locations?.join(", ") ||
              "Hyderabad/Secunderabad"}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Expected salary</p>
          <p
            className={
              data.expected_salary ? "text-gray-800" : "text-blue-600"
            }
          >
            {data.expected_salary || "Add expected salary"}
          </p>
        </div>
      </div>

      {/* Dialog for Editing */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Career Profile</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Current Industry</Label>
              <Input
                value={formData.current_industry}
                onChange={(e) =>
                  setFormData({ ...formData, current_industry: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Department</Label>
              <Input
                value={formData.department}
                onChange={(e) =>
                  setFormData({ ...formData, department: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Role Category</Label>
              <Input
                value={formData.role_category}
                onChange={(e) =>
                  setFormData({ ...formData, role_category: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Job Role</Label>
              <Input
                value={formData.job_role}
                onChange={(e) =>
                  setFormData({ ...formData, job_role: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Desired Job Type</Label>
              <Input
                value={formData.desired_job_type}
                onChange={(e) =>
                  setFormData({ ...formData, desired_job_type: e.target.value })
                }
                placeholder="e.g. Permanent, Contract"
              />
            </div>

            <div>
              <Label>Desired Employment Type</Label>
              <Input
                value={formData.desired_employment_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    desired_employment_type: e.target.value,
                  })
                }
                placeholder="e.g. Full Time, Part Time"
              />
            </div>

            <div>
              <Label>Preferred Shift</Label>
              <Input
                value={formData.preferred_shift}
                onChange={(e) =>
                  setFormData({ ...formData, preferred_shift: e.target.value })
                }
                placeholder="e.g. Day, Night, Flexible"
              />
            </div>

            <div>
              <Label>Expected Salary</Label>
              <Input
                value={formData.expected_salary}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expected_salary: e.target.value,
                  })
                }
                placeholder="e.g. 10-15 LPA"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
